import { useState, type ReactNode } from 'react';
import { Button, Upload, Space, Modal, App, Table, Alert, Typography, Input, Select } from 'antd';
import { UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import type { WorkBook } from 'xlsx';
import { readWorkbook, downloadTemplate } from '../lib/excel';

interface ParseOut<T> {
  items: T[];
  errors: { rowIndex: number; reason: string }[];
}

export interface PreviewColumn {
  title: string;
  dataIndex: string;
  /** 预设值列：改为下拉选择；不传则为可直接编辑的文本框 */
  editor?: {
    type: 'select';
    options: { value: string | number | boolean; label: string }[];
    allowClear?: boolean;
  };
}

interface Props<T, C = void> {
  /** 模板文件名（不含扩展名）*/
  templateName: string;
  templateHeaders: string[];
  templateSample?: string[][];
  /** 解析首个工作表 */
  parse: (wb: WorkBook, sheetName: string) => ParseOut<T>;
  /** 本次导入开始前执行一次，返回值作为 importOne 的第二参数（如跨行缓存）*/
  prepare?: () => C | Promise<C>;
  /** 逐行导入；失败请 throw Error(原因) */
  importOne: (item: T, ctx: C) => Promise<void>;
  /** 全部完成后回调（用于刷新列表）*/
  onDone?: () => void;
  disabled?: boolean;
  /** 导入按钮文案 */
  label?: string;
  /** 预览表列（不传则按对象键自动生成，均为可编辑文本）*/
  previewColumns?: PreviewColumn[];
  /** 行校验：返回问题原因则该行标红警告（如邮箱在用户管理不存在）；返回 null 表示通过。随编辑实时重算 */
  validateRow?: (row: Record<string, unknown>) => string | null;
}

type Row = Record<string, unknown>;

export function ExcelImportButton<T, C = void>({
  templateName,
  templateHeaders,
  templateSample,
  parse,
  prepare,
  importOne,
  onDone,
  disabled,
  label = '导入 Excel',
  previewColumns,
  validateRow,
}: Props<T, C>) {
  const { message } = App.useApp();
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<{ rowIndex: number; reason: string }[]>([]);
  const [rows, setRows] = useState<Row[] | null>(null); // 可编辑草稿；null=未打开

  // 选文件 → 只解析，弹出可编辑预览（不立即导入）
  const handleFile = async (file: File) => {
    try {
      const wb = await readWorkbook(file);
      const sheet = wb.SheetNames[0];
      if (!sheet) {
        message.error('文件无有效工作表');
        return;
      }
      const parsed = parse(wb, sheet);
      setErrors(parsed.errors);
      setRows((parsed.items as Row[]).map((it) => ({ ...it })));
    } catch {
      message.error('读取文件失败（请用 .xls / .xlsx）');
    }
  };

  const updateCell = (index: number, key: string, val: unknown) => {
    setRows((prev) => (prev ? prev.map((r, i) => (i === index ? { ...r, [key]: val } : r)) : prev));
  };

  const close = () => {
    if (busy) return;
    setRows(null);
    setErrors([]);
  };

  // 确认后才逐行导入（用编辑后的值）
  const doImport = async () => {
    if (!rows) return;
    setBusy(true);
    const key = 'excel-import';
    try {
      const ctx = (prepare ? await prepare() : undefined) as C;
      const failed: string[] = [];
      let ok = 0;
      for (let i = 0; i < rows.length; i++) {
        message.open({
          key,
          type: 'loading',
          content: `导入中 ${i + 1}/${rows.length}…`,
          duration: 0,
        });
        try {
          await importOne(rows[i] as T, ctx);
          ok += 1;
        } catch (e) {
          failed.push((e as Error).message || `第 ${i + 1} 条导入失败`);
        }
      }
      message.destroy(key);
      setRows(null);
      setErrors([]);

      const skipReasons = errors.map((e) => `第${e.rowIndex}行：${e.reason}`);
      const allIssues = [...skipReasons, ...failed];
      if (allIssues.length === 0) {
        message.success(`导入完成：成功 ${ok} 条`);
      } else {
        Modal.info({
          title: `导入完成：成功 ${ok} 条，未成功 ${allIssues.length} 条`,
          width: 560,
          content: (
            <div style={{ maxHeight: 320, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
              {allIssues.join('\n')}
            </div>
          ),
        });
      }
      onDone?.();
    } finally {
      message.destroy(key);
      setBusy(false);
    }
  };

  const items = rows ?? [];
  const cols: PreviewColumn[] =
    previewColumns ??
    Object.keys(items[0] ?? {})
      .filter((k) => k !== '__row')
      .map((k) => ({ title: k, dataIndex: k }));

  const rowIssue = (r: Row): string | null => (validateRow ? validateRow(r) : null);
  const invalidCount = validateRow ? items.filter((r) => rowIssue(r)).length : 0;

  const renderCell = (c: PreviewColumn) => (value: unknown, _row: Row, index: number) => {
    if (c.editor?.type === 'select') {
      return (
        <Select
          size="small"
          style={{ width: '100%' }}
          value={value as string | number | boolean | undefined}
          allowClear={c.editor.allowClear ?? false}
          options={c.editor.options}
          onChange={(v) => updateCell(index, c.dataIndex, v)}
        />
      );
    }
    return (
      <Input
        size="small"
        value={value == null ? '' : String(value)}
        onChange={(e) => updateCell(index, c.dataIndex, e.target.value)}
      />
    );
  };

  return (
    <>
      <Space size="small">
        <Button
          icon={<DownloadOutlined />}
          onClick={() => downloadTemplate(templateName, templateHeaders, templateSample ?? [])}
        >
          下载模板
        </Button>
        <Upload
          accept=".xls,.xlsx"
          showUploadList={false}
          disabled={disabled || busy}
          beforeUpload={(file) => {
            void handleFile(file as unknown as File);
            return false; // 阻止自动上传，改为预览确认
          }}
        >
          <Button icon={<UploadOutlined />} disabled={disabled ?? false}>
            {label}
          </Button>
        </Upload>
      </Space>

      <Modal
        title="导入预览"
        open={rows !== null}
        width={860}
        onCancel={close}
        onOk={doImport}
        okText={items.length > 0 ? `确认导入 ${items.length} 条` : '确认导入'}
        okButtonProps={{ disabled: items.length === 0, loading: busy }}
        cancelButtonProps={{ disabled: busy }}
        cancelText="取消"
        destroyOnClose
      >
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Alert
            type={
              items.length === 0 || invalidCount > 0
                ? invalidCount > 0
                  ? 'warning'
                  : 'error'
                : errors.length > 0
                  ? 'warning'
                  : 'success'
            }
            showIcon
            message={
              items.length === 0
                ? '未解析到有效数据，请检查表头与内容是否符合模板'
                : `解析到 ${items.length} 条有效数据${errors.length > 0 ? `，另有 ${errors.length} 行将跳过` : ''}${invalidCount > 0 ? `；其中 ${invalidCount} 行有问题（见红色行），可直接在下方修正` : '，可直接修改后确认导入'}`
            }
          />
          {errors.length > 0 && (
            <div
              style={{
                maxHeight: 120,
                overflow: 'auto',
                background: '#fff7e6',
                padding: 8,
                borderRadius: 4,
              }}
            >
              {errors.map((e, i) => (
                <Typography.Text key={i} type="warning" style={{ display: 'block', fontSize: 12 }}>
                  第{e.rowIndex}行：{e.reason}
                </Typography.Text>
              ))}
            </div>
          )}
          {items.length > 0 && (
            <Table<Row>
              size="small"
              rowKey={(_, i) => String(i)}
              dataSource={items}
              onRow={(r) => (rowIssue(r) ? { style: { background: '#fff1f0' } } : {})}
              columns={[
                ...cols.map((c) => ({
                  title: c.title,
                  dataIndex: c.dataIndex,
                  render: renderCell(c),
                })),
                ...(validateRow
                  ? [
                      {
                        title: '校验',
                        key: '__issue',
                        width: 200,
                        render: (_: unknown, r: Row) => {
                          const issue = rowIssue(r);
                          return issue ? (
                            <Typography.Text type="danger" style={{ fontSize: 12 }}>
                              {issue}
                            </Typography.Text>
                          ) : (
                            <Typography.Text type="success">✓</Typography.Text>
                          );
                        },
                      },
                    ]
                  : []),
              ]}
              pagination={items.length > 8 ? { pageSize: 8, size: 'small' } : false}
              scroll={{ y: 320 }}
            />
          )}
        </Space>
      </Modal>
    </>
  );
}
